import type { Except, ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import { useCallback, useState } from 'react'

import { LazyTextInput } from '../../forms/lazyTextInput/LazyTextInput'
import type { RawLazyTextInputProps } from '../../forms/lazyTextInput/RawLazyTextInput'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikLazyTextInputProps = Except<
  RawLazyTextInputProps,
  'touched' | 'defaultValue' | 'onBlur'
> & {
  name: ValidatedKey
  defaultValue?: string
  onBlur?: (value: string | undefined) => void
} & Except<FormikInputPropsForWrapper, 'validationText'>

const FormikLazyTextInputInternal = ({
  name,
  label,
  isRequired,
  description,
  defaultValue,
  onDefaultValueClick,
  onError,
  disabled,
  onBlur,
  onChange,
  hasRightColumnAlways,
  existingValueConfig,
  className,
  ...restProps
}: FormikLazyTextInputProps) => {
  const [currentError, setCurrentError] = useState<Error>()
  const [field, meta, { setValue }] = useField({ name })
  const { onChange: _, ...actualField } = field
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as string

  const { isInError, error } = useFormikErrorWithState({
    ...meta,
    // Fetch errors have priority over validation errors.
    error: currentError ? currentError.message : meta.error,
  })

  const handleChange = useCallback(
    async (value: string | undefined) => {
      await setValue(value, true)

      onChange?.(value)

      // We only propagate blur event onChange because user may tab away
      // before the lazy text input has had a chance to finish resolving
      // the user input. Our code uses `onBlur` more as a "value settled"
      // callback, rather than "user has moved away from the input field".
      onBlur?.(value)
    },
    [setValue, onBlur, onChange],
  )

  const handleError = useCallback(
    (error: Error | undefined) => {
      setCurrentError(error)
      onError?.(error)
      // No need to `setError` to Formik because the field's `validate` will already report it.
    },
    [onError],
  )

  const inputValueDifferenceFromExistingValue =
    useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <LazyTextInput
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      touched={meta.touched}
      placeholder={defaultValue}
      onChange={handleChange}
      onError={handleError}
      disabled={isFieldDisabled}
      {...actualField}
      {...restProps}
    />
  )
}

export const FormikLazyTextInput = withFormikInputDiffing(
  FormikLazyTextInputInternal,
)
